import { Children, cloneElement, isValidElement, useMemo, useState } from "react";
import { useUIDSeed } from "react-uid";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { isNumeric } from "../../../../helpers/utils";
import { Button, buttonRoundedVariants, buttonSizes, buttonVariants } from "../../../Button";
import { withErrorBoundary } from "../../../ErrorBoundary";
import { ChevronDownIcon, Icon, PlusIcon } from "../../../Icon";
import { pillVariants } from "../Pill";
import styles from "./PillsList.style";

const PillsListStyled = styled.section((props) => ({ ...styles(props) }));

const pillListVariants = Object.freeze({
    PRIMARY: pillVariants.PRIMARY,
    SECONDARY: pillVariants.SECONDARY,
});

const pillButtonVariants = Object.freeze({
    PRIMARY: buttonVariants.QUATERNARY,
    SECONDARY: buttonVariants.PRIMARY,
});

const PillsList = ({
    className,
    children,
    title,
    titleTagComponent: TitleTag,
    limit,
    hasAddTagButton,
    hasMarginTop,
    paddingBottom,
    onAddTagButtonClick,
    hasShowMoreButton,
    onShowMoreButtonClick,
    addTagTitle,
    itemsSpacing,
    variant,
    isDishTags,
    ...rest
}) => {
    const listElements = useMemo(
        () => Children.toArray(children).filter((child) => isValidElement(child)),
        [children],
    );
    const [tagsLimit, setTagsLimit] = useState(limit);

    const uid = useUIDSeed();

    // Slice children array if limit is set
    const tags = useMemo(() => {
        if (isNumeric(tagsLimit) && tagsLimit < listElements?.length) {
            return listElements.slice(0, tagsLimit);
        }
        return listElements;
    }, [tagsLimit, children, listElements]);

    if (!listElements.length && !hasAddTagButton) {
        return null;
    }

    return (
        <PillsListStyled
            itemsSpacing={itemsSpacing}
            className={className}
            hasMarginTop={hasMarginTop}
            variant={variant}
            paddingBottom={paddingBottom}
            isDishTags={isDishTags}
            {...rest}
        >
            {title && <TitleTag className="tagPills_title">{title}</TitleTag>}
            <ul className="tagPills_list">
                {tags.map((child, index) => {
                    const { id } = child.props;
                    return (
                        <li key={uid(id || index)} className="tagPills_listItem">
                            {cloneElement(child, {
                                variant: pillListVariants[variant.toUpperCase()],
                            })}
                        </li>
                    );
                })}

                {hasShowMoreButton && isNumeric(tagsLimit) && tagsLimit < listElements?.length && (
                    <li className="tagPills_listItem">
                        <Button
                            title="Pokaži sve"
                            onClick={() => setTagsLimit(undefined)}
                            variant={buttonVariants.OCTONARY}
                            size={buttonSizes.SMALL}
                            rounded={buttonRoundedVariants.LARGER}
                            iconRightComponent={<Icon icon={ChevronDownIcon} />}
                            className="tagPills_cta isMore"
                        >
                            Pokaži sve
                        </Button>
                    </li>
                )}

                {hasAddTagButton && typeof onAddTagButtonClick === "function" && (
                    <li className="tagPills_listItem">
                        <Button
                            title={addTagTitle}
                            onClick={onAddTagButtonClick}
                            variant={pillButtonVariants[variant.toUpperCase()]}
                            size={buttonSizes.SMALL}
                            rounded={buttonRoundedVariants.LARGER}
                            iconRightComponent={<Icon rotate={180} icon={PlusIcon} />}
                            className="tagPills_cta"
                        >
                            {addTagTitle}
                        </Button>
                    </li>
                )}
            </ul>
        </PillsListStyled>
    );
};

PillsList.propTypes = {
    className: PropTypes.string,
    title: PropTypes.string,
    titleTagComponent: PropTypes.elementType,
    limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    hasAddTagButton: PropTypes.bool,
    hasMarginTop: PropTypes.bool,
    onAddTagButtonClick: PropTypes.func,
    hasShowMoreButton: PropTypes.bool,
    onShowMoreButtonClick: PropTypes.func,
    addTagTitle: PropTypes.string,
    itemsSpacing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    variant: PropTypes.oneOf([...Object.values(pillListVariants)]),
    paddingBottom: PropTypes.number,
    isDishTags: PropTypes.bool,
};

PillsList.defaultProps = {
    className: undefined,
    title: undefined,
    titleTagComponent: "div",
    limit: undefined,
    hasAddTagButton: false,
    onAddTagButtonClick: undefined,
    hasShowMoreButton: false,
    onShowMoreButtonClick: undefined,
    addTagTitle: "Dodaj tag",
    variant: pillListVariants.PRIMARY,
    itemsSpacing: 10,
    hasMarginTop: false,
    paddingBottom: undefined,
    isDishTags: false,
};

export { pillListVariants };

export default withErrorBoundary(PillsList);
